import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import cn from './cn.js';
import Spinner from './Spinner.js';

const variants = {
  secondary: 'bg-white text-gray-500 ring-1 ring-inset ring-gray-200 shadow-sm hover:bg-gray-50 hover:text-gray-900',
  ghost: 'text-gray-500 hover:bg-gray-100 hover:text-gray-900',
  soft: 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100',
  primary: 'bg-indigo-600 text-white shadow-sm hover:bg-indigo-500',
  danger: 'bg-white text-gray-500 ring-1 ring-inset ring-gray-200 shadow-sm hover:bg-red-50 hover:text-red-600 hover:ring-red-200',
  dangerGhost: 'text-gray-500 hover:bg-red-50 hover:text-red-600',
  inverted: 'text-indigo-100 hover:bg-white/10 hover:text-white',
};

const sizes = {
  xs: 'h-7 w-7',
  sm: 'h-8 w-8',
  md: 'h-9 w-9',
  lg: 'h-10 w-10',
};

const iconSizes = {
  xs: 'h-3.5 w-3.5',
  sm: 'h-4 w-4',
  md: 'h-4.5 w-4.5 [&>svg]:h-[18px] [&>svg]:w-[18px]',
  lg: 'h-5 w-5',
};

/**
 * IconButton: a square button with only an icon. `label` is required and is
 * used for both the accessible name and the native tooltip.
 */
const IconButton = forwardRef(function IconButton(
  { icon: Icon, label, variant = 'secondary', size = 'md', loading = false, className, to, type, disabled, ...props },
  ref,
) {
  const classes = cn(
    'inline-flex shrink-0 items-center justify-center rounded-lg transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60',
    variants[variant] || variants.secondary,
    sizes[size] || sizes.md,
    className,
  );
  const iconClass = cn(
    size === 'md' ? 'h-[18px] w-[18px]' : iconSizes[size] || iconSizes.md,
    'shrink-0',
  );

  const content = loading ? <Spinner className={iconClass} /> : <Icon className={iconClass} aria-hidden="true" />;

  if (to) {
    return (
      <Link ref={ref} to={to} className={classes} aria-label={label} title={label} {...props}>
        {content}
      </Link>
    );
  }

  return (
    <button
      ref={ref}
      type={type || 'button'}
      className={classes}
      aria-label={label}
      title={label}
      disabled={disabled || loading}
      {...props}
    >
      {content}
    </button>
  );
});

export default IconButton;
